/**
 * Veeso Asset API definitions
 */

import { defHTTP } from '../../api-builder';
import type {
  Asset,
  CreateAssetRequest,
  CreateAssetResponse,
  GetAssetsParams,
  UpdateAssetRequest,
  RemoveBackgroundRequest,
  RemoveBackgroundResponse,
  UpscaleImageRequest,
  ImageGenerateRequest,
  ImageGenerateResponse,
  GenerateImageWithRefsRequest,
  ImageGenerateV2Request,
  ImageEditV2Request,
  ImageJobResponse,
  ImageToolV2Request,
  VideoGenerateRequest,
  VideoTaskResponse,
  VideoStatusResponse,
} from './types';

/**
 * Asset API definitions for use with apiBuilder
 */
export const assetAPIDefinitions = {
  /**
   * Create a new asset
   */
  createAsset: defHTTP<CreateAssetRequest, CreateAssetResponse>('POST', '/asset'),

  /**
   * Get list of assets
   */
  getAssets: defHTTP<GetAssetsParams, Asset[]>('GET', '/asset'),

  /**
   * Get a single asset by ID
   */
  getAsset: defHTTP<{ id: string }, Asset>('GET', '/asset/:id'),

  /**
   * Update an asset by ID
   */
  updateAsset: defHTTP<{ id: string } & UpdateAssetRequest, Asset>('PUT', '/asset/:id'),

  /**
   * Remove background from an image
   */
  removeBackground: defHTTP<RemoveBackgroundRequest, RemoveBackgroundResponse>(
    'POST',
    '/v1/asset/background_removal'
  ),

  /**
   * Upscale an image
   */
  upscaleImage: defHTTP<UpscaleImageRequest, Asset>('POST', '/v1/image/upscale'),

  /**
   * Generate an image using AI
   */
  generateImage: defHTTP<ImageGenerateRequest, ImageGenerateResponse>('POST', '/v1/image/asset'),

  /**
   * Generate an image asynchronously using the v2 job queue.
   */
  generateImageV2: defHTTP<ImageGenerateV2Request, ImageJobResponse>('POST', '/v2/image/generate'),

  /**
   * Edit an image asynchronously using the v2 job queue.
   */
  editImageV2: defHTTP<ImageEditV2Request, ImageJobResponse>('POST', '/v2/image/edit'),

  /**
   * Upscale an image asynchronously using the v2 job queue.
   */
  upscaleImageV2: defHTTP<ImageToolV2Request, ImageJobResponse>('POST', '/v2/image/upscale'),

  /**
   * Remove an image background asynchronously using the v2 job queue.
   */
  removeBackgroundV2: defHTTP<ImageToolV2Request, ImageJobResponse>(
    'POST',
    '/v2/image/background-removal'
  ),

  /**
   * Vectorize an image asynchronously using the v2 job queue.
   */
  vectorizeImageV2: defHTTP<ImageToolV2Request, ImageJobResponse>('POST', '/v2/image/vectorize'),

  /**
   * Get an async job by ID.
   */
  getJob: defHTTP<{ jobId: string }, ImageJobResponse>('GET', '/v2/job/:jobId'),

  /**
   * Cancel an async job by ID.
   */
  cancelJob: defHTTP<{ jobId: string }, ImageJobResponse>('DELETE', '/v2/job/:jobId'),

  /**
   * Generate an image using AI, like `generateImage` but this api accepts reference images
   */
  generateImageWithRefs: defHTTP<GenerateImageWithRefsRequest, ImageGenerateResponse>(
    'POST',
    '/v1/image/edit'
  ),

  /**
   * Create a video generation task
   */
  createVideoTask: defHTTP<VideoGenerateRequest, VideoTaskResponse>('POST', '/v1/videos'),

  /**
   * Get video generation task status
   */
  getVideoStatus: defHTTP<{ id: string }, VideoStatusResponse>('GET', '/v1/videos/:id'),
};
